import React, {useMemo} from 'react';
import {Platform} from 'react-native';
import {Provider as PaperProvider} from "react-native-paper";
import {Provider as ReduxProvider} from "react-redux";
import {PersistGate} from "redux-persist/integration/react";
import {NavigationContainer, DefaultTheme as NavDefaultTheme, DarkTheme as NavDarkTheme} from "@react-navigation/native";
import {SafeAreaProvider, initialWindowMetrics} from "react-native-safe-area-context";
import {Root} from "react-native-popup-confirm-toast";
import {BluetoothProvider} from "../helpers/BluetoothContext";
import {TableProvider} from "../helpers/TableContext";
import SystemProvider from "../helpers/SystemContext";
import CartProvider from "../helpers/CartContext";
import OrdersProvider from "../helpers/OrdersContext";
import {VipCardProvider} from '../helpers/VipCardContext';
import OnlineOrderProvider from '../helpers/OnlineOrderContext';
import {CurrentScreenProvider} from '../helpers/CurrentScreenContext';
import {TourProvider} from "../helpers/TourContext";
import ErrorBoundary from 'react-native-error-boundary';
import ErrorFallback from './ErrorFallback';
import {getPaperTheme} from "../core/theme";
import OfflineSyncProvider from '../helpers/OfflineSyncContext';
import PendingOrderDialog from '../helpers/PendingOrderDialog';
import {ThemeProvider as CustomThemeProvider, useTheme} from '../helpers/ThemeContext';
import {TimeTrackingProvider} from '../helpers/TimeTrackingContext';
import {ServiceModeProvider} from '../helpers/ServiceModeContext';
import {WorkSheetProvider} from '../helpers/WorkSheetContext';
import {MailProvider} from '../helpers/MailContext';
import {TeamChatProvider} from '../helpers/teamChat/TeamChatContext';
import {navigationRef, recordCurrentRoute} from '../helpers/navigationRef';
import AppTutorialOverlay from './AppTutorialOverlay';
import MiniAppBridge from './miniApp/MiniAppBridge';
import {HappyAlertHost} from './HappyAlert';
import DemoModeBadge from './DemoModeBadge';
import AppLockGate from './AppLockGate';
import {getStateFromPath as adminGetStateFromPath, ADMIN_LINKING_PREFIXES} from '../helpers/deepLink/adminLinkResolver';
import {getInitialURL as adminGetInitialURL, subscribe as adminSubscribe} from '../helpers/deepLink/adminLinkNavigation';

/**
 * Universal Links (admin/services/app.happysolutions.hu) + the app's custom
 * URL schemes. Defined once at module scope (not inside the component) so
 * React Navigation's internal `useLinking` effects see a stable object
 * reference across renders instead of re-subscribing on every render.
 *
 * `getStateFromPath` is a deliberate no-op here — the real routing (which
 * needs the host, and an async management-access check) happens inside
 * `getInitialURL` / `subscribe` themselves. See the doc comment atop
 * `helpers/deepLink/adminLinkResolver.js` for the full reasoning, and
 * `helpers/deepLink/adminLinkNavigation.js` for the actual dispatch. This
 * coexists with the pre-existing `helpers/widgets/widgetDeepLink.js` handler
 * (still wired independently in App.jsx) — that one keeps owning
 * `happyapp://widget/...` links unchanged.
 *
 * `enabled: false` on web only: RN Navigation's *web* `useLinking` (unlike
 * native) ignores this config's `getInitialURL`/`subscribe` and installs its
 * own popstate listener + default state-to-URL syncing. On browser
 * back/forward without a matching in-memory history record (always true
 * after a full page reload), it calls our `getStateFromPath` — a deliberate
 * no-op returning `undefined` — then hits an unguarded
 * `resetRoot(getStateFromPath(path))` (useLinking.js:179-181), which crashes
 * with "Cannot read properties of undefined (reading 'routes')" and a white
 * screen (Sentry HAPPY-APP-12). Web already never used `getInitialURL`/
 * `subscribe` here (routing is imperative via `handleAdminLinkUrl`), so
 * disabling the linking config on web removes both the crash and the
 * broken/no-op URL-bar sync, with zero native impact.
 */
const linking = {
  enabled: Platform.OS !== 'web',
  prefixes: ADMIN_LINKING_PREFIXES,
  getStateFromPath: adminGetStateFromPath,
  getInitialURL: adminGetInitialURL,
  subscribe: adminSubscribe,
};

/**
 * Inner tree that consumes the custom theme and feeds both design systems a
 * dark-aware theme:
 *  - react-native-paper `PaperProvider` gets the light/dark Paper theme, so
 *    Paper components (TextInput, Button, …) render legibly in dark mode.
 *  - React Navigation's `NavigationContainer` gets a theme derived from the app
 *    palette, so navigator/header/card backgrounds flip instead of staying white.
 * It lives BELOW `CustomThemeProvider` on purpose — that's the only place
 * `useTheme()` is available.
 */
const ThemedProviders = ({store, persistor, errorHandler, children}) => {
  const {theme, isDark} = useTheme();

  const paperTheme = useMemo(() => getPaperTheme(isDark), [isDark]);

  const navTheme = useMemo(() => {
    const base = isDark ? NavDarkTheme : NavDefaultTheme;

    return {
      ...base,
      dark: isDark,
      colors: {
        ...base.colors,
        primary: theme.colors.primary,
        background: theme.colors.background,
        card: theme.colors.card,
        text: theme.colors.text,
        border: theme.colors.border,
        notification: theme.colors.notification,
      },
    };
  }, [theme, isDark]);

  return (
    <PaperProvider theme={paperTheme}>
      {/*
        The service-module providers (TimeTracking / WorkSheet) live INSIDE the
        ErrorBoundary on purpose: if any of their effects or a consuming screen
        throws, the boundary catches it, shows ErrorFallback and reports the
        crash — instead of an uncatchable white screen for service technicians.
      */}
      <ErrorBoundary
        onError={errorHandler}
        FallbackComponent={ErrorFallback}
      >
        <TimeTrackingProvider>
          <ServiceModeProvider>
          <WorkSheetProvider>
            <TourProvider>
              <NavigationContainer
                ref={navigationRef}
                theme={navTheme}
                linking={linking}
                onStateChange={() => recordCurrentRoute(navigationRef.getCurrentRoute())}
              >
                <Root>
                  <ReduxProvider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                      <CurrentScreenProvider>
                        <OrdersProvider>
                          <OnlineOrderProvider>
                            <SystemProvider>
                              <VipCardProvider>
                                <TableProvider>
                                  <CartProvider>
                                    <BluetoothProvider>
                                      <OfflineSyncProvider>
                                        <MailProvider>
                                          {/* Team Chat state for the selected store (unread
                                              badge in every app mode); inside Redux so it can
                                              follow store switches. */}
                                          <TeamChatProvider>
                                            {children}
                                            {/* Desktop mini apps only: tray clock / dock badge (renders null elsewhere). */}
                                            <MiniAppBridge />
                                            <PendingOrderDialog />
                                            <HappyAlertHost />
                                          </TeamChatProvider>
                                        </MailProvider>
                                      </OfflineSyncProvider>
                                    </BluetoothProvider>
                                  </CartProvider>
                                </TableProvider>
                              </VipCardProvider>
                            </SystemProvider>
                          </OnlineOrderProvider>
                        </OrdersProvider>
                      </CurrentScreenProvider>
                    </PersistGate>
                  </ReduxProvider>
                </Root>
              </NavigationContainer>
              <AppTutorialOverlay />
              <DemoModeBadge />
              <AppLockGate />
            </TourProvider>
          </WorkSheetProvider>
          </ServiceModeProvider>
        </TimeTrackingProvider>
      </ErrorBoundary>
    </PaperProvider>
  );
};

/**
 * AppProviders component that wraps the app with all necessary providers
 * This component is memoized to prevent unnecessary re-renders
 */
const AppProviders = ({ store, persistor, errorHandler, children }) => {
  return (
    // SafeAreaProvider is the OUTERMOST provider so every header, screen wrapper
    // and React-Navigation header below it gets real notch / Dynamic Island
    // insets. `initialWindowMetrics` seeds the correct values on the very first
    // frame, avoiding a header "jump" on cold start.
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <CustomThemeProvider>
        <ThemedProviders store={store} persistor={persistor} errorHandler={errorHandler}>
          {children}
        </ThemedProviders>
      </CustomThemeProvider>
    </SafeAreaProvider>
  );
};

export default React.memo(AppProviders);
